import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Announcement } from '../models/announcement.model';
import { MockDataService } from './mock-data.service';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementService {
  private mockDataService = inject(MockDataService);

  constructor() {}

  getAll(): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    return of([...data.announcements]);
  }

  getById(id: number): Observable<Announcement | undefined> {
    const data = this.mockDataService.createDb();
    const announcement = data.announcements.find((a: Announcement) => a.id === id);
    return of(announcement);
  }

  create(
    announcement: Omit<Announcement, 'id' | 'createdDate' | 'updatedDate'>
  ): Observable<Announcement> {
    const data = this.mockDataService.createDb();
    const newAnnouncement: Announcement = {
      ...announcement,
      id: this.getNextId(data.announcements),
      createdDate: new Date(),
      updatedDate: new Date(),
    };

    return of(newAnnouncement);
  }

  update(id: number, announcement: Partial<Announcement>): Observable<Announcement | undefined> {
    const data = this.mockDataService.createDb();
    const foundAnnouncement = data.announcements.find((a: Announcement) => a.id === id);
    if (foundAnnouncement) {
      const updatedAnnouncement = {
        ...foundAnnouncement,
        ...announcement,
        id,
        updatedDate: new Date(),
      };
      return of(updatedAnnouncement);
    }
    return of(undefined);
  }

  delete(id: number): Observable<boolean> {
    const data = this.mockDataService.createDb();
    const foundAnnouncement = data.announcements.find((a: Announcement) => a.id === id);

    return of(!!foundAnnouncement);
  }

  getActive(): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const activeAnnouncements = data.announcements.filter((a: Announcement) => a.isActive);
    return of([...activeAnnouncements]);
  }

  getByType(type: string): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const filteredAnnouncements = data.announcements.filter((a: Announcement) => a.type === type);
    return of([...filteredAnnouncements]);
  }

  getByPriority(priority: string): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const filteredAnnouncements = data.announcements.filter(
      (a: Announcement) => a.priority === priority
    );
    return of([...filteredAnnouncements]);
  }

  getByAuthor(author: string): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const filteredAnnouncements = data.announcements.filter(
      (a: Announcement) => a.author === author
    );
    return of([...filteredAnnouncements]);
  }

  searchByContent(searchTerm: string): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const filteredAnnouncements = data.announcements.filter(
      (a: Announcement) =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return of([...filteredAnnouncements]);
  }

  getByDateRange(startDate: Date, endDate: Date): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const filteredAnnouncements = data.announcements.filter(
      (a: Announcement) => a.createdDate >= startDate && a.createdDate <= endDate
    );
    return of([...filteredAnnouncements]);
  }

  getRecent(): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentAnnouncements = data.announcements.filter(
      (a: Announcement) => a.createdDate >= thirtyDaysAgo && a.isActive
    );
    return of([...recentAnnouncements]);
  }

  getLatestAnnouncements(count: number = 5): Observable<Announcement[]> {
    const data = this.mockDataService.createDb();
    const latestAnnouncements = data.announcements
      .filter((a: Announcement) => a.isActive)
      .sort((a: Announcement, b: Announcement) => b.createdDate.getTime() - a.createdDate.getTime())
      .slice(0, count);
    return of([...latestAnnouncements]);
  }

  private getNextId(announcements: Announcement[]): number {
    return Math.max(...announcements.map((a: Announcement) => a.id)) + 1;
  }
}
